import { logger } from "@utils/logger";
import { SegmentEvents } from "./constants";

const MAX_RETRIES = 3 as const;
const RETRY_DELAY = 500 as const;

const getBaseTrackingData = () => ({
  isAutomatedTest: false,
  isCypress: false,
  isDatadog: false,
  isAWS: false,
  isBot: false,
  isMobileDevice: !!window._GROWTHERAPY_IS_MOBILE,
});

const parseCookie = (name: string): string | undefined => {
  const nameWithEquals = `${name}=`;
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(nameWithEquals))
    ?.slice(nameWithEquals.length);
};

const sendTrackingEvent = (name: string, data: Record<string, unknown>): Promise<string> => {
  let retries = 0;
  
  return new Promise((resolve, reject) => {
    function waitForAnalytics() {
      if (window.analytics) {
        const vwo_uuid = parseCookie('_vwo_uuid_v2');
        const growUniversalId = parseCookie('grow_universal_id');
        const trackingData = {
          ...data,
          growUniversalId,
          vwo_uuid
        };
        
        window.analytics.track(name, trackingData, {}, () => resolve('success'));
      } else if (retries < MAX_RETRIES) {
        retries += 1;
        setTimeout(waitForAnalytics, RETRY_DELAY);
      } else {
        reject('Analytics object not present on window.');
      }
    }

    waitForAnalytics();
  });
};

export const setupAnchorLinkClickTracking = (
  elements: HTMLCollectionOf<HTMLAnchorElement>,
  name: string,
  getElementId: (element: HTMLAnchorElement) => string | undefined,
  modifyUrl: boolean = false,
): void => {
  const windowPathname = window.location.pathname;
  const hostname = modifyUrl ? new URL(window.location.href).hostname : null;
  
  Array.from(elements).forEach((element) => {
    try {
      const elementId = getElementId(element);
      if (!elementId) return;
      
      if (modifyUrl) {
        const url = hostname === "growtherapy.com"
          ? element.href
          : element.href.replace("growtherapy.com", "growtherapystaging.com");
        element.href = url;
      }

      const linkHrefPathname = new URL(element.href).pathname;
      const destinationUrl = linkHrefPathname !== windowPathname ? element.href : undefined;
      
      const trackingData = {
        elementId,
        ...(destinationUrl && { destinationUrl }),
        ...getBaseTrackingData(),
      };
      
      element.addEventListener("click", () => {
        sendTrackingEvent(name, trackingData).catch((error) => {
          logger(`[2965] Failed to track anchor link click: ${error}`);
        });
      });
    } catch (error) {
      if (error instanceof Error) {
        logger(`[2965] Failed to setup tracking for element: ${error.message}`);
      }
    }
  });
};

export const trackButtonClick = (eventType: string) => {
  try {
    const trackingData = {
      event_type: eventType,
      ...getBaseTrackingData(),
    };
    
    sendTrackingEvent(SegmentEvents.USER_CLICKED, trackingData).catch((error) => {
      logger(`[2965] Failed to track button click: ${error}`);
    });
  } catch (error) {
    if (error instanceof Error) {
      logger(`[2965] Failed to setup tracking for element: ${error.message}`);
    }
  }
}

export const trackUserInput = (
  name: string,
  inputValue: string,
) => {
  try {
    const trackingData = {
      additional_properties: inputValue,
      event_type: name,
      ...getBaseTrackingData(),
    };
    
    sendTrackingEvent(SegmentEvents.USER_INPUTTED, trackingData).catch((error) => {
      logger(`[2965] Failed to track input: ${error}`);
    });
  } catch (error) {
    if (error instanceof Error) {
      logger(`[2965] Failed to setup tracking for element: ${error.message}`);
    }
  }
};
